import React, { useState } from "react";
import { Logo } from "../common/Logo.js";
import { StorageService } from "../../services/storage.js";
import {
  ShieldCheck,
  Lock,
  Mail,
  UserPlus,
  KeyRound,
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff
} from "lucide-react";

export const AdminLogin = ({ settings, onLoginSuccess, onBackToSite }) => {
  const [authMode, setAuthMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [regRole, setRegRole] = useState("editor");
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = (e) => {
    if (e) e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    setLoading(true);
    setTimeout(() => {
      const result = StorageService.login(email, password);
      setLoading(false);
      if (result.success && result.user) {
        onLoginSuccess(result.user);
      } else {
        setError(result.error || "Authentication failed. Please check your credentials.");
      }
    }, 250);
  };

  const handleRegister = (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    if (!regName.trim() || !regEmail.trim() || !regPassword) {
      setError("Please fill in all registration fields.");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      const res = StorageService.registerUser({
        name: regName,
        email: regEmail,
        password: regPassword,
        role: regRole
      });
      setLoading(false);
      if (res.success && res.user) {
        setSuccessMsg(`Account created for "${res.user.name}". Important: Your account is currently LOCKED and will remain locked until the Super Administrator approves and activates it.`);
        setEmail(regEmail);
        setPassword(regPassword);
        setAuthMode("login");
      } else {
        setError(res.error || "Could not register user.");
      }
    }, 300);
  };

  return (
    <div className="min-h-screen bg-stone-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-4">
          <Logo
            size="lg"
            logoUrl={settings?.logo_url}
            siteName={settings?.site_name}
            siteNameBn={settings?.site_name_bn}
          />
        </div>
        <h2 className="text-center text-2xl font-serif font-bold text-slate-900 tracking-tight">
          Admin Portal Authentication
        </h2>
        <p className="mt-1 text-center text-xs text-slate-600">
          {settings?.site_name || "Baitul Muqaddas Model Madrasah"} · Dynamic Content Management System
        </p>
      </div>

      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md px-4 sm:px-0">
        <div className="bg-white py-8 px-6 shadow-sm border border-stone-200 rounded-2xl sm:px-10 space-y-6">
          <div className="flex items-center p-1 bg-stone-100 rounded-xl">
            <button
              type="button"
              onClick={() => {
                setAuthMode("login");
                setError(null);
              }}
              className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                authMode === "login" ? "bg-white text-emerald-950 shadow-xs" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Staff Sign In
            </button>
            <button
              type="button"
              onClick={() => {
                setAuthMode("register");
                setError(null);
              }}
              className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                authMode === "register" ? "bg-white text-emerald-950 shadow-xs" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <UserPlus className="w-3.5 h-3.5 text-emerald-700" />
              <span>Create New User</span>
            </button>
          </div>

          {error && (
            <div
              className={`p-3.5 rounded-xl text-xs flex items-start gap-2.5 animate-in fade-in ${
                error.toLowerCase().includes("locked") || error.toLowerCase().includes("approval")
                  ? "bg-amber-50 border border-amber-300 text-amber-950"
                  : "bg-red-50 border border-red-200 text-red-800"
              }`}
            >
              {error.toLowerCase().includes("locked") ? (
                <Lock className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
              ) : (
                <AlertCircle className="w-4 h-4 shrink-0 text-red-600 mt-0.5" />
              )}
              <div>
                <strong className="block font-bold mb-0.5">
                  {error.toLowerCase().includes("locked")
                    ? "Account Locked (Super Admin Approval Required)"
                    : "Authentication Failed"}
                </strong>
                <span>{error}</span>
              </div>
            </div>
          )}

          {successMsg && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-xs flex items-center gap-2 animate-in fade-in">
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
              <span>{successMsg}</span>
            </div>
          )}

          {authMode === "login" && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Staff Email Address *
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@baitulmuqaddas.edu.bd"
                    className="w-full pl-9 pr-3.5 py-2 text-xs rounded-lg border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type={showLoginPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-9 pr-10 py-2 text-xs rounded-lg border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                    title={showLoginPassword ? "Hide password" : "Show password"}
                  >
                    {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 px-4 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>{loading ? "Authenticating..." : "Sign In to Admin Portal"}</span>
              </button>
            </form>
          )}

          {authMode === "register" && (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Full Name & Title *
                </label>
                <input
                  type="text"
                  required
                  value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  placeholder="e.g. Mufti Kamal Uddin"
                  className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Staff Email Address *
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="kamal@baitulmuqaddas.edu.bd"
                    className="w-full pl-9 pr-3.5 py-2 text-xs rounded-lg border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  System Role *
                </label>
                <select
                  value={regRole}
                  onChange={(e) => setRegRole(e.target.value)}
                  className="w-full px-2.5 py-2 text-xs rounded-lg border border-stone-300 bg-white"
                >
                  <option value="editor">Editor (Can create & edit content)</option>
                  <option value="teacher">Teacher / Ustadh (Curriculum & academic updates)</option>
                  <option value="principal">Principal / Muhtamim (Academic & administrative leader)</option>
                  <option value="moderator">Moderator (Can review admissions & inquiries)</option>
                  <option value="accountant">Accountant (Accounts & administrative records)</option>
                  <option value="super_admin">Super Admin (Full administrative access)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type={showRegPassword ? "text" : "password"}
                    required
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    placeholder="Enter secure password..."
                    className="w-full pl-9 pr-10 py-2 text-xs rounded-lg border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegPassword(!showRegPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                    title={showRegPassword ? "Hide password" : "Show password"}
                  >
                    {showRegPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-amber-900 text-xs flex items-center gap-2">
                <Lock className="w-4 h-4 text-amber-700 shrink-0" />
                <span>Security Notice: Newly registered accounts will remain locked until approved by the Super Administrator.</span>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 px-4 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <KeyRound className="w-4 h-4" />
                <span>{loading ? "Registering Account..." : "Create Account (Locked Until Approved)"}</span>
              </button>
            </form>
          )}

          <div className="text-center pt-2">
            <button
              onClick={onBackToSite}
              className="text-xs text-slate-500 hover:text-emerald-800 font-medium inline-flex items-center gap-1.5 cursor-pointer"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Return to Public Website</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
